import i18n from "../../../locales/i18n";
import {
  firstImageOfListingTemplateInput,
  includeBsadInput,
  listingPrice,
  mainListingInput,
  mainTemplateColorInput,
  realtorImageTemplateInput,
  includeRealtorImageInput,
  realtorLogoTemplateInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  secondImageOfListingTemplateInput,
  thirdImageOfListingTemplateInput,
  fourthImageOfListingTemplateInput,
  getBsadPrompt,
  getRealtorImagePrompt,
  getVerticalFooterPrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  hasRealtorImage,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const property_collage_var_1: templateDataType = {
  templateName: "קולאז' נכס - רשת תמונות",
  displayImageUrl: "/static/creative-template-display-images/real-estate-property-collage-1.jpg",
  description: "פלייר אלגנטי עם רשת תמונות של 2-4 תמונות, כותרת בולטת ופרטי הנכס המרכזיים",
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorImageTemplateInput,
    includeRealtorImageInput,
    realtorLogoTemplateInput,
    firstImageOfListingTemplateInput,
    secondImageOfListingTemplateInput,
    thirdImageOfListingTemplateInput(false), // not required for this template
    fourthImageOfListingTemplateInput(false), // not required for this template
    {
      ...listingPrice,
      required: false,
      description: `${listingPrice.description} - לא חובה. יש להשאיר ריק אם לא רוצים להציג מחיר`
    }, // not required for this template
    {
      label: "כותרת ראשית",
      type: "text",
      nameInPrompt: "main_headline",
      showInSection: "template_specific",
      description: "הכותרת הראשית הבולטת שתופיע בחלק העליון",
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        const roomsCount = mainListing?.roomsCount ?? "X";
        return `דירת ${roomsCount} חדרים במחיר שלא יחזור`;
      }
    },
    {
      required: true,
      label: "תווית שכונה",
      type: "text",
      nameInPrompt: "label_1",
      showInSection: "template_specific",
      description: "שם השכונה או מידע נוסף אחר",
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return `שכונת ${mainListing?.address?.neighborhood ?? "____"}`;
      }
    },
    {
      required: true,
      label: "תווית #2",
      type: "text",
      nameInPrompt: "label_2",
      showInSection: "template_specific",
      description: "מידע נוסף",
      defaultValue: (params: promptGeneratorInitialParams) => {
        return "חניה";
      }
    },
    {
      required: false,
      label: "תווית #3",
      type: "text",
      nameInPrompt: "label_3",
      showInSection: "template_specific",
      description: "מידע נוסף",
      defaultValue: (params: promptGeneratorInitialParams) => {
        return "מחסן";
      }
    },
    {
      required: false,
      label: "תווית #4",
      type: "text",
      nameInPrompt: "label_4",
      showInSection: "template_specific",
      description: "מידע נוסף",
      defaultValue: (params: promptGeneratorInitialParams) => {
        return "נוף לים";
      }
    }
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, lighterColorValue, darkerColorValue } = getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    // Build images array - filter out empty strings but keep track of which images we have
    const allImages = [
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image")
        ?.finalValue,
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image_2")
        ?.finalValue,
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image_3")
        ?.finalValue,
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image_4")
        ?.finalValue
    ];

    const labels = [
      (textParams.find((param) => param.nameInPrompt === "label_1")?.finalValue as string) ?? "",
      (textParams.find((param) => param.nameInPrompt === "label_2")?.finalValue as string) ?? "",
      (textParams.find((param) => param.nameInPrompt === "label_3")?.finalValue as string) ?? "",
      (textParams.find((param) => param.nameInPrompt === "label_4")?.finalValue as string) ?? ""
    ].filter((label) => label && label.trim() !== "");

    const imageCount = allImages.filter(
      (img) => img && typeof img === "string" && img.trim() !== ""
    ).length;
    const gridType =
      imageCount === 2 ? "two_column" : imageCount === 3 ? "three_balanced" : "four_quadrant";

    const price = textParams.find((param) => param.nameInPrompt === "listing_price")?.finalValue;
    return `
{
  "description": "Real Estate Property Collage Flyer",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Professional, elegant, premium, lifestyle-focused",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "three_section_vertical",
    "sections": [
      {
        "name": "header_section",
        "height": "20%",
        "background_color": "stylized radiant ${mainColorValue} color with minor gradient accents",
        "background_pattern": {
          "type": "subtle_buildings_overlay",
          "opacity": 0.08,
          "style": "Very subtle, stylized building silhouettes for texture without distraction"
        },
        "content": [
          {
            "type": "main_headline",
            "text": "${
              textParams.find((param) => param.nameInPrompt === "main_headline")?.finalValue
            }",
            "style": {
              "font_color": "white",
              "font_size": "large",
              "font_weight": "bold",
              "text_align": "center",
              "padding": "medium"
            }
          },
          {
            "type": "labels_row",
            "content": [
              ${labels.map((label) => `"${label}"`).join(", ")}
            ],
            "style": {
              "font_color": "${lighterColorValue}",
              "font_size": "small",
              "font_weight": "medium",
              "text_align": "center",
              "layout": "horizontal_equal_spacing",
              "separator": " • "
            }
          }
        ]
      },
      {
        "name": "images_grid_section",
        "height": "65%",
        "background_color": "white",
        "content": {
          "type": "image_grid",
          "grid_layout": "${gridType}",
          "images": [
            ${
              imageCount >= 1
                ? `{
              "source": "listing_image",
              "style": "cover, equal_size"
            }`
                : ""
            }${
      imageCount >= 2
        ? `,
            {
              "source": "listing_image_2", 
              "style": "cover, equal_size"
            }`
        : ""
    }${
      imageCount >= 3
        ? `,
            {
              "source": "listing_image_3",
              "style": "cover, equal_size"
            }`
        : ""
    }${
      imageCount >= 4
        ? `,
            {
              "source": "listing_image_4",
              "style": "cover, equal_size"
            }`
        : ""
    }
          ],
          "spacing": "thin_white_gaps",
          "corner_radius": "slight",
          "description": "Balanced grid layout showcasing ${imageCount} property images"
        }
      },
       ${
         hasRealtorImage(params)
           ? `${getRealtorImagePrompt(
               params,
               "The realtor should be positioned in the bottom corner, overlapping the footer to create visual depth."
             )},`
           : ""
       }
      ${getVerticalFooterPrompt(
        params,
        price
          ? ` {
            "type": "price_highlight",
            "price": "${price}",
            "style": {
              "font_color": "${mainColorValue}",
              "font_size": "extra_large",
              "font_weight": "bold",
              "text_align": "center",
              "margin_bottom": "small"
            },
            "description": "Price prominently displayed in bold ${mainColorValue}. Should be placed within the footer section${
              hasRealtorImage(params)
                ? " between the realtor image on one side and the logo and contact info on the other side"
                : " with the logo and contact info"
            }."
          }`
          : null
      )},
        ]
      }
    ]
  },

  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    ${imageCount >= 2 ? `"listing_image_2": "${getInputImageLabel("listing_image_2")}",` : ""}
    ${imageCount >= 3 ? `"listing_image_3": "${getInputImageLabel("listing_image_3")}",` : ""}
    ${imageCount >= 4 ? `"listing_image_4": "${getInputImageLabel("listing_image_4")}",` : ""}
    ${hasRealtorImage(params) ? `"realtor_image": "${getInputImageLabel("realtor_image")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  "important_notes": [
    "Header background pattern must be extremely subtle to maintain text readability",
    "Image grid should automatically adapt to ${imageCount} images with balanced composition",
    "Maintain clean, professional aesthetics with minimal visual clutter",
    "Price should be prominently displayed and immediately eye-catching",
    "Labels row should be clearly readable against the colored background",
    "DO NOT alter provided images beyond cropping/resizing for grid layout"
  ]
}`;
  }
};
